import type { GroupSubtitleProps } from './types'
import type { Pricings } from '~/composables/types/event'
import { formatSmartPriceAmount } from '~/utils/currencyHelpers'

const PRICE_PER_PARTICIPANT_KEY = 'product.show.checkout.pricePerParticipant'
const PER_PARTICIPANT_KEY = 'product.show.checkout.perParticipant'

/**
 * Calculates per participant price: total price / quantity
 * Returns formatted string e.g. "200 €"
 * @param localeBCP47 - Locale in BCP47 format (e.g. "de-DE"), already converted by caller
 */
export const getGroupPerParticipantPriceFormatted = (
  pricingData: Pricings | undefined,
  quantity: number,
  localeBCP47: string
): string | undefined => {
  if (!pricingData?.price?.total_price || quantity <= 0) return undefined

  const totalAmount = Number(pricingData.price.total_price.amount)
  const currency = pricingData.price.total_price.currency || 'EUR'
  const perParticipantAmount = Math.round(totalAmount / quantity)

  return formatSmartPriceAmount(perParticipantAmount, localeBCP47, currency)
}

/**
 * Calculates original (pre-promotion) per participant price: base_price / quantity
 * Returns undefined when there is no discount to avoid showing unnecessary strikethrough.
 * @param localeBCP47 - Locale in BCP47 format (e.g. "de-DE"), already converted by caller
 */
export const getGroupOriginalPerParticipantPriceFormatted = (
  pricingData: Pricings | undefined,
  quantity: number,
  localeBCP47: string
): string | undefined => {
  if (!pricingData?.price?.base_price || quantity <= 0) return undefined

  const baseAmount = Number(pricingData.price.base_price.amount)
  const totalAmount = Number(
    pricingData.price.total_price?.amount ?? baseAmount
  )

  if (baseAmount <= totalAmount) return undefined

  const currency = pricingData.price.base_price.currency || 'EUR'
  const perParticipantAmount = Math.round(baseAmount / quantity)

  return formatSmartPriceAmount(perParticipantAmount, localeBCP47, currency)
}

export const getGroupSubtitleProps = (
  perParticipantFormatted: string | undefined,
  originalPerParticipantFormatted: string | undefined,
  hasSale: boolean,
  t: (key: string, params?: Record<string, unknown>) => string
): GroupSubtitleProps => {
  if (!perParticipantFormatted) {
    return {
      variant: 'group',
      formattedSubtitle: undefined,
      perPersonKey: PER_PARTICIPANT_KEY,
      hasSale,
    }
  }

  let priceHtml = perParticipantFormatted
  if (originalPerParticipantFormatted && hasSale) {
    priceHtml = `<span class="line-through text-gray-400">${originalPerParticipantFormatted}</span> ${perParticipantFormatted}`
  }

  const tempPlaceholder = '__PRICE_PLACEHOLDER__'
  const translationWithPlaceholder = t(PRICE_PER_PARTICIPANT_KEY, {
    price: tempPlaceholder,
  })

  return {
    variant: 'group',
    formattedSubtitle: translationWithPlaceholder.replace(
      tempPlaceholder,
      priceHtml
    ),
    perPersonKey: PER_PARTICIPANT_KEY,
    hasSale,
  }
}
